// Add this hook to a separate file, e.g., useAutoSave.js
import { useCallback, useEffect, useRef, useState } from 'react';

export function useAutoSave({
  saveFunction,
  data,
  enabled = true,
  saveOnUnmount = true,
  interval = 60000, // 1 minute
  inactivityDelay = 30000 // 30 seconds
}) {
  const [lastSaveTime, setLastSaveTime] = useState(Date.now());
  const [isSaving, setIsSaving] = useState(false);
  const [lastError, setLastError] = useState(null);
  const [lastActivity, setLastActivity] = useState(Date.now());
  
  // Use refs to prevent dependency changes
  const dataRef = useRef(data);
  const enabledRef = useRef(enabled);
  const intervalRef = useRef(interval);
  const inactivityDelayRef = useRef(inactivityDelay);
  const lastActivityRef = useRef(lastActivity);
  const lastSaveTimeRef = useRef(lastSaveTime);
  const isComponentMounted = useRef(true);
  const intervalIdRef = useRef(null);
  
  // Update refs when props change
  useEffect(() => {
    dataRef.current = data;
  }, [data]);
  
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);
  
  useEffect(() => {
    intervalRef.current = interval;
  }, [interval]);
  
  useEffect(() => {
    inactivityDelayRef.current = inactivityDelay;
  }, [inactivityDelay]);
  
  // Update activity time ref
  useEffect(() => {
    lastActivityRef.current = lastActivity;
  }, [lastActivity]);
  
  // Update save time ref
  useEffect(() => {
    lastSaveTimeRef.current = lastSaveTime;
  }, [lastSaveTime]);
  
  // Track user activity
  useEffect(() => {
    if (!enabled) return;
    
    const updateActivity = () => {
      const now = Date.now();
      lastActivityRef.current = now;
      setLastActivity(now);
    };
    
    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('keypress', updateActivity);
    window.addEventListener('click', updateActivity);
    window.addEventListener('scroll', updateActivity);
    
    return () => {
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('keypress', updateActivity);
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('scroll', updateActivity);
    };
  }, [enabled]);
  
  // Save function with stable dependencies
  const save = useCallback(async () => {
    if (isSaving || !enabledRef.current || !isComponentMounted.current) {
      console.log("Save skipped: already saving, disabled, or unmounted");
      return;
    }
    
    console.log("Starting auto-save...");
    setIsSaving(true);
    
    try {
      await saveFunction({ isAutoSave: true });
      const now = Date.now();
      console.log("Auto-save completed at", new Date(now).toLocaleTimeString());
      lastSaveTimeRef.current = now;
      setLastSaveTime(now);
      setLastError(null);
    } catch (error) {
      console.error("Auto-save failed:", error);
      setLastError(error);
    } finally {
      if (isComponentMounted.current) {
        setIsSaving(false);
      }
    }
  }, [isSaving, saveFunction]);
  
  // Store save function in ref to ensure stability
  const saveRef = useRef(save);
  useEffect(() => {
    saveRef.current = save;
  }, [save]);
  
  // Setup the timer once with empty dependency array - only runs on mount
  useEffect(() => {
    console.log("AUTO-SAVE HOOK MOUNTED");
    isComponentMounted.current = true;
    
    const checkAndSave = () => {
      if (!isComponentMounted.current) return;
      if (!enabledRef.current) return;
      
      const now = Date.now();
      const timeSinceActivity = now - lastActivityRef.current;
      const timeSinceSave = now - lastSaveTimeRef.current;
      
      //console.log(`Auto-save check at ${new Date(now).toLocaleTimeString()}`);
      //console.log(`Time since activity: ${Math.round(timeSinceActivity/1000)}s (threshold: ${Math.round(inactivityDelayRef.current/1000)}s)`);
      //console.log(`Time since save: ${Math.round(timeSinceSave/1000)}s (threshold: ${Math.round(intervalRef.current/1000)}s)`);
      
      let shouldSave = false;
      
      // Save if inactive for the specified delay
      if (timeSinceActivity > inactivityDelayRef.current) {
        console.log("Triggering save due to inactivity");
        shouldSave = true;
      }
      
      // Save if interval has passed since last save
      if (timeSinceSave > intervalRef.current) {
        console.log("Triggering save due to interval");
        shouldSave = true;
      }
      
      if (shouldSave) {
        saveRef.current();
      }
    };
    
    // Wait 10 seconds before first check to avoid immediate save
    const initialTimeout = setTimeout(() => {
      console.log("Initial auto-save check after 10s delay");
      checkAndSave();
      
      // Set up fixed interval (15 seconds) for checks
      console.log("Setting up 15-second interval for auto-save checks");
      intervalIdRef.current = setInterval(checkAndSave, 15000);
    }, 10000);
    
    // Clean up function runs only when component unmounts
    return () => {
      console.log("AUTO-SAVE HOOK UNMOUNTING");
      isComponentMounted.current = false;
      
      clearTimeout(initialTimeout);
      
      if (intervalIdRef.current) {
        console.log("Clearing auto-save interval");
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
      
      if (saveOnUnmount && enabledRef.current) {
        console.log("Saving on unmount");
        saveRef.current();
      }
    };
  }, []); // Empty dependency array - only run on mount and unmount
  
  // Return values and functions
  return {
    lastSaveTime,
    isSaving,
    lastError,
    saveNow: save
  };
}

// Then in your component:
